import { get_token } from "@/router";
import {
  get_active_organization_id,
  get_active_profile_id,
  get_organization_branding,
  get_organization_profile_branding,
} from "@/utils/organization_profile";
import {
  request_organization_access_options,
  request_organization_members,
} from "./organization_members_api";
import {
  first_array,
  normalize_member,
  normalize_organisation_feature,
  parse_json_or_null,
} from "./organization_members_helpers";
import organization_members_datatable_methods from "./organization_members_datatable_methods";

const INCIDENT_REPORT_PERMISSION_NAMES = new Set([
  "assign",
  "escalate",
  "export",
]);

export default {
  ...organization_members_datatable_methods,
  get_token() {
    return get_token();
  },
  set_active_profile(profile) {
    this.active_profile = profile || null;
    this.saved_branding = this.get_saved_branding();
    this.fetch_organization_access_options();
    this.fetch_organization_members();
    this.fetch_organization_companies();
  },
  get_active_profile_id() {
    return get_active_profile_id(this.active_profile || {});
  },
  get_company_id() {
    return (
      get_active_organization_id() ||
      null
    );
  },
  get_branding_context() {
    return ({
      organization_id: get_active_organization_id(),
      profile_id: this.get_active_profile_id(),
      user_id: this.user_details?.user_id,
    });
  },
  get_saved_branding() {
    const profile_id = this.get_active_profile_id() || this.user_details?.user_id || "current";
    return [
      this.get_context_branding(),
      this.get_profile_branding(),
      this.get_stored_branding(profile_id),
    ].find(Boolean) || null;
  },
  get_context_branding() {
    return get_organization_branding(this.get_branding_context());
  },
  get_profile_branding() {
    return get_organization_profile_branding(
      this.active_profile?.company || this.active_profile || {},
      this.user_details || {}
    );
  },
  get_stored_branding(profile_id) {
    return parse_json_or_null(localStorage.getItem(`organization_branding_${profile_id}`));
  },
  get_member_payload(extra = {}) {
    const payload = {
      organization_id: get_active_organization_id(),
    };
    if (!payload.organization_id) {
      payload.profile_id = this.get_active_profile_id();
    }
    return Object.assign(payload, extra);
  },
  async fetch_organization_access_options() {
    if (!this.has_member_lookup_context()) return;
    this.is_loading_access_options = true;
    try {
      const response = await request_organization_access_options();
      this.apply_organization_access_options(response);
    } catch {
      this.show_error("Could not load organisation access options");
    } finally {
      this.is_loading_access_options = false;
    }
  },
  apply_organization_access_options(response = {}) {
    if (response?.status !== "success") return;

    this.organization_permissions = this.normalize_organization_permissions(response.permission_types);
    this.organization_features = this.normalize_organization_features(
      response.features,
      this.organization_permissions
    );
    this.ensure_default_access_feature();
  },
  normalize_organization_features(features, permission_types = []) {
    if (!Array.isArray(features)) return [];
    return features
      .map(normalize_organisation_feature)
      .map((feature) => this.add_incident_permission_fallback(feature, permission_types))
      .filter((feature) => String(feature.key).toLowerCase() !== "assets");
  },
  add_incident_permission_fallback(feature, permission_types = []) {
    if (String(feature?.key || "").toLowerCase() !== "incident_reports") return feature;

    const available_permissions = Array.isArray(feature.permissions) && feature.permissions.length
      ? feature.permissions
      : permission_types;

    return ({
      ...feature,
      permissions: available_permissions.filter((permission) => (
        INCIDENT_REPORT_PERMISSION_NAMES.has(String(permission?.name || "").trim().toLowerCase())
      )),
    });
  },
  normalize_organization_permissions(permission_types) {
    return Array.isArray(permission_types) ? permission_types : [];
  },
  ensure_default_access_feature() {
    if (!Array.isArray(this.member_access.features)) this.member_access.features = [];
  },
  normalize_query(value) {
    return String(value || "").trim().toLowerCase();
  },
  user_matches_query(user, query) {
    return [
      user?.first_name,
      user?.last_name,
      user?.email,
      user?.full_name,
      user?.user?.first_name,
      user?.user?.last_name,
      user?.user?.email,
    ].some((value) => String(value || "").toLowerCase().includes(query));
  },
  get_members_from_response(response) {
    const members = first_array([
      response?.data?.members,
      response?.data?.organization_members,
      response?.members,
      response?.organization_members,
      response?.data,
    ]);
    return Array.isArray(members) ? members.map(normalize_member) : [];
  },
  has_member_lookup_context() {
    return !!this.get_token() && !!(get_active_organization_id() );
  },
  has_members_response(response) {
    return !!(
      response?.status === "success" ||
      response?.data ||
      response?.members ||
      response?.organization_members
    );
  },
  async fetch_organization_members() {
    if (!this.has_member_lookup_context()) return;
    this.destroy_members_datatable();
    this.destroy_invitations_datatable();
    this.is_loading_members = true;
    try {
      const response = await request_organization_members(this.get_member_payload());
      if (this.has_members_response(response)) {
        this.organization_members = this.get_members_from_response(response);
      }
    } catch {
      this.show_error("Could not load organisation members");
    } finally {
      this.is_loading_members = false;
      this.refresh_members_datatable();
      this.refresh_invitations_datatable();
    }
  },
};
